import { useMyGroups } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupList } from '@/components/group-list';
import { GroupLoadFailed, GroupLoading } from '@/components/group-load-state';
import { NoGroups } from '@/components/no-groups';

export const Route = createFileRoute('/_app/account_/groups')({ component: MyGroupsPage });

/**
 * Account › My groups: the person's groups under the Stronger Memory group, from Core, each
 * opening its Manage group. With none, Figma global/empty: the character with a group card, an
 * invitation to start one, "Create new group" and "Go back".
 */
function MyGroupsPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const groups = useMyGroups();
  const list = groups.data ?? [];
  const createGroup = () => void navigate({ to: '/account/groups/new' });
  const toolbar = (
    <ToolbarBack
      render={({ className, children }) => (
        <Link to="/account" className={className}>
          {children}
        </Link>
      )}
    />
  );
  if (groups.isPending) {
    return (
      <>
        {toolbar}
        <GroupLoading />
      </>
    );
  }
  if (groups.isError) {
    return (
      <>
        {toolbar}
        <div className="pt-content-md">
          <GroupLoadFailed onRetry={() => void groups.refetch()} />
        </div>
      </>
    );
  }
  if (list.length > 0) {
    return (
      <>
        {toolbar}
        <h1 className="text-display-sm font-bold">
          <Trans>My groups</Trans>
        </h1>
        <div className="pt-content-md">
          <GroupList groups={list} />
        </div>
        <div className="flex justify-end pt-content-md">
          <Button onClick={createGroup}>{t`Create new group`}</Button>
        </div>
      </>
    );
  }
  return (
    <>
      {toolbar}
      <NoGroups
        body={
          <Trans>Start a group to exercise alongside people you know or help facilitate.</Trans>
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => void navigate({ to: '/account' })}>
              {t`Go back`}
            </Button>
            <Button onClick={createGroup}>{t`Create new group`}</Button>
          </>
        }
      />
    </>
  );
}
